import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, RotateCcw, Timer, Layers } from "lucide-react";

/**
 * /admin/match-formats — per-stage referee-format control.
 * Persists to config.stage_formats. New matches created for that stage
 * inherit the format (snapshot on `POST /matches`). Existing matches keep
 * their original format so historical scoring stays consistent.
 */
const STAGES = [
  { key: "group",   label: "League Stage" },
  { key: "quarter", label: "Quarterfinals" },
  { key: "semi",    label: "Semifinals" },
  { key: "final",   label: "Grand Final" },
];

const DEFAULT_STAGE = { format_type: "set_based", sets_per_match: 2, games_per_set: 6, tiebreak: false };

export default function MatchFormats() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tieOrder, setTieOrder] = useState([2, 1, 3]);
  const [orderBusy, setOrderBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: order }] = await Promise.all([
        api.get("/config"),
        api.get("/config/tie-match-order").catch(() => ({ data: { tie_match_order: [2, 1, 3] } })),
      ]);
      setCfg(data);
      setTieOrder(order?.tie_match_order || [2, 1, 3]);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const stageFormats = cfg?.stage_formats || {};
  const setStage = (key, patch) => {
    setCfg((c) => ({
      ...c,
      stage_formats: {
        ...(c?.stage_formats || {}),
        [key]: { ...(c?.stage_formats?.[key] || DEFAULT_STAGE), ...patch },
      },
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/config", { stage_formats: cfg.stage_formats });
      toast.success("Match formats saved. New matches will inherit these.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resetToDefaults = () => {
    setCfg((c) => ({
      ...c,
      stage_formats: {
        group:   { format_type: "set_based", sets_per_match: 2, games_per_set: 6, tiebreak: false },
        quarter: { format_type: "set_based", sets_per_match: 3, games_per_set: 6, tiebreak: true },
        semi:    { format_type: "set_based", sets_per_match: 3, games_per_set: 6, tiebreak: true },
        final:   { format_type: "set_based", sets_per_match: 3, games_per_set: 6, tiebreak: true },
      },
    }));
  };

  if (!cfg) return <div className="p-8 text-zinc-500" data-testid="mf-loading">Loading config…</div>;

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="match-formats">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Tournament Setup</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white leading-none">
            Match Formats
          </h1>
          <div className="text-xs text-zinc-500 mt-2 max-w-2xl">
            Choose <b className="text-white/80">Time-Based</b> (existing engine) or
            <b className="text-white/80"> Traditional Set-Based</b> per stage.
            Newly created matches inherit these settings — the referee's UI adapts automatically.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults} className="border-white/20 text-white" data-testid="mf-reset">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset to Defaults
          </Button>
          <Button onClick={save} disabled={busy} className="bg-[#F4B400] text-black font-bold" data-testid="mf-save">
            <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {STAGES.map((s) => (
          <StageCard
            key={s.key}
            stageKey={s.key}
            title={s.label}
            value={stageFormats[s.key] || DEFAULT_STAGE}
            onChange={(patch) => setStage(s.key, patch)}
          />
        ))}
      </div>

      <Card className="mt-6 p-5 bg-[#0d1a13] border-white/10" data-testid="mf-tie-order-card">
        <div className="flex items-center gap-2 mb-2">
          <Layers className="w-5 h-5 text-[#F4B400]" />
          <div className="font-anton text-2xl uppercase text-white">Team-Tie Play Order</div>
        </div>
        <div className="text-xs text-zinc-500 mb-4 max-w-2xl">
          Every team tie contains 3 seed matches. Season 3 default plays them in the order <b className="text-white/80">Seed 2 → Seed 1 → Seed 3</b>. Only Super Admin can change this. The referee court page auto-serves matches in this order.
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {[[2,1,3], [1,2,3], [3,2,1], [3,1,2], [1,3,2], [2,3,1]].map((opt) => {
            const key = opt.join("-");
            const active = tieOrder.join("-") === key;
            return (
              <button
                key={key}
                onClick={() => setTieOrder(opt)}
                data-testid={`mf-tie-order-${key}`}
                className={`px-3 py-2 rounded-lg border transition font-anton text-lg tabular-nums tracking-widest ${
                  active
                    ? "border-[#F4B400] bg-[#F4B400]/10 text-[#F4B400]"
                    : "border-white/10 bg-black/30 text-zinc-300 hover:border-white/30"
                }`}
              >
                {opt.map((s, i) => (
                  <span key={i}>
                    Seed {s}{i < opt.length - 1 ? <span className="text-zinc-500 mx-1">→</span> : null}
                  </span>
                ))}
              </button>
            );
          })}
          <Button
            onClick={async () => {
              setOrderBusy(true);
              try {
                await api.put("/config/tie-match-order", { tie_match_order: tieOrder });
                toast.success(`Tie play order saved: Seed ${tieOrder.join(" → Seed ")}. Applies to newly created ties.`);
              } catch (e) { toast.error(formatApiError(e)); }
              finally { setOrderBusy(false); }
            }}
            disabled={orderBusy}
            className="bg-[#F4B400] text-black font-bold uppercase tracking-widest"
            data-testid="mf-tie-order-save"
          >
            <Save className="w-4 h-4 mr-2" /> {orderBusy ? "Saving…" : "Save Order"}
          </Button>
        </div>

        {/* Live preview: a sample tie rendered exactly the way the referee & broadcast see it */}
        <div className="mt-5 p-4 rounded-lg border border-white/10 bg-black/40" data-testid="mf-tie-order-preview">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Live Preview · Sample Tie</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {tieOrder.map((seed, i) => (
              <div key={`preview-${i}`} className="flex items-center gap-3 p-3 rounded border border-white/10 bg-[#0f1a15]" data-testid={`mf-tie-order-preview-${i + 1}`}>
                <div className="w-10 h-10 rounded-full bg-[#F4B400]/10 border border-[#F4B400]/40 flex items-center justify-center font-anton text-lg text-[#F4B400]">{i + 1}</div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Match {i + 1}</div>
                  <div className="text-sm font-bold text-white tabular-nums">Seed {seed} <span className="text-zinc-500 mx-1">vs</span> Seed {seed}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-zinc-500 mt-3 leading-relaxed">
            Referees receive the matches in this order — Match 1 is scored first. Pre-reveal, the draw shows only these seed labels. Once both team lineups are locked, the corresponding player pair names replace the labels.
          </div>
        </div>
      </Card>
    </div>
  );
}

function StageCard({ stageKey, title, value, onChange }) {
  const isSet = value.format_type === "set_based";
  return (
    <Card className="p-5 bg-[#0d1a13] border-white/10" data-testid={`mf-stage-${stageKey}`}>
      <div className="flex items-center gap-2 mb-3">
        {isSet ? <Layers className="w-5 h-5 text-[#F4B400]" /> : <Timer className="w-5 h-5 text-[#F4B400]" />}
        <div className="font-anton text-2xl uppercase text-white">{title}</div>
      </div>

      <div className="space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Format</div>
          <Select value={value.format_type} onValueChange={(v) => onChange({ format_type: v })}>
            <SelectTrigger data-testid={`mf-format-${stageKey}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="set_based">Traditional Set-Based</SelectItem>
              <SelectItem value="time_based">Time-Based (existing)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isSet ? (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Number of Sets</div>
              <Select value={String(value.sets_per_match)} onValueChange={(v) => onChange({ sets_per_match: parseInt(v) })}>
                <SelectTrigger data-testid={`mf-sets-${stageKey}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Set</SelectItem>
                  <SelectItem value="2">2 Sets</SelectItem>
                  <SelectItem value="3">3 Sets</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Games per Set</div>
              <div className="flex gap-2">
                {[4, 6].map(n => (
                  <Button
                    key={n}
                    variant={value.games_per_set === n ? "default" : "outline"}
                    className={value.games_per_set === n ? "bg-[#F4B400] text-black" : "border-white/20 text-white"}
                    onClick={() => onChange({ games_per_set: n })}
                    data-testid={`mf-games-${stageKey}-${n}`}
                  >
                    {n} Games
                  </Button>
                ))}
                <Input
                  type="number"
                  min={1}
                  max={12}
                  value={value.games_per_set}
                  onChange={(e) => onChange({ games_per_set: parseInt(e.target.value) || 6 })}
                  className="w-20 bg-black/40 border-white/10 text-white"
                  data-testid={`mf-games-custom-${stageKey}`}
                />
                <div className="text-[10px] text-zinc-500 self-center">Custom</div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-1">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-zinc-400">Allow Tiebreak (7-6)</div>
                <div className="text-[10px] text-zinc-500">Adds +1 to max game score per set</div>
              </div>
              <Switch checked={!!value.tiebreak} onCheckedChange={(v) => onChange({ tiebreak: v })} data-testid={`mf-tiebreak-${stageKey}`} />
            </div>
            <div className="text-xs text-zinc-400 mt-2 p-2 rounded bg-black/30 border border-white/5" data-testid={`mf-preview-${stageKey}`}>
              Referee will see: <b className="text-white">{value.sets_per_match} × {value.games_per_set}-game set{value.sets_per_match > 1 ? "s" : ""}{value.tiebreak ? " · tiebreak" : ""}</b>
            </div>
          </>
        ) : (
          <div className="text-xs text-zinc-400 p-2 rounded bg-black/30 border border-white/5">
            Referee uses the existing time-based engine (periods, shot clock, points).
            Time-based match config is edited in <b className="text-white">Admin → Config</b>.
          </div>
        )}
      </div>
    </Card>
  );
}
